import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, Breadcrumbs, Link as MuiLink,
  Divider, Stack, Button, TextField, Chip, Alert, Dialog, DialogContent,
  CircularProgress, InputAdornment, IconButton, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, VerifiedUser, Receipt, Speed,
  ShieldOutlined, AccountBalanceWallet, PersonOutlined,
  EmailOutlined, PhoneOutlined, LanguageOutlined, KeyOutlined,
  Visibility, VisibilityOff, Close, LocalOffer, CurrencyRupee,
} from '@mui/icons-material';
import { C } from './vps/theme';
import { BASE_URL, API_ENDPOINTS } from '../config/api.config';

// ─── STATIC PLAN DEFINITION ──────────────────────────────────────────────────
// Change these values here to adjust the product shown on /checkout-1.
// No router state or navigation from a plans page is needed.
const STATIC_PLAN = {
  name: 'NVMe 4 VPS Hosting',
  category: 'Self-Managed VPS',
  monthlyRate: 396,           // price per month in INR (before GST)
  specsText: '2 vCPU Cores · 4 GB DDR5 RAM · 100GB NVMe SSD · Unmetered Bandwidth',
  features: [
    '2 vCPU Cores & 4 GB DDR5 RAM',
    '100 GB High-Throughput NVMe Storage',
    'Full Root SSH & API Access',
    'Claude Code Pre-installed',
    'DDoS Protection & Dedicated IP',
    'Unmetered Bandwidth & 99.99% Uptime',
  ],
};
// ─────────────────────────────────────────────────────────────────────────────

const osOptions = [
  'AlmaLinux 9 Enterprise (Recommended)',
  'Ubuntu 24.04 LTS Developer',
  'Debian 12 Stable',
  'CentOS Stream 9',
  'Rocky Linux 9',
];

const billingCycleOptions = [
  { value: '1',  label: '1 Month',   months: 1  },
  { value: '12', label: '12 Months', months: 12 },
  { value: '24', label: '24 Months', months: 24 },
];

const CheckoutDirect = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    serverHostname: '',
    selectedOS: '',
  });
  const [billingCycle, setBillingCycle] = useState('1');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMessage, setCouponMessage] = useState({ type: '', text: '' });

  const [customAmount, setCustomAmount] = useState('');
  const [amountError, setAmountError] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loadingCashfree, setLoadingCashfree] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [formError, setFormError] = useState('');

  // The amount the user actually typed — used directly as the Cashfree charge amount.
  const parsedAmount = parseFloat(customAmount);
  const payableAmount = !isNaN(parsedAmount) && parsedAmount > 0 ? parsedAmount : 0;

  const handleAmountChange = (e) => {
    const val = e.target.value;
    // Allow only digits and a single decimal point
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setCustomAmount(val);
      if (amountError) setAmountError('');
    }
  };

  // Pricing calculation
  const selectedCycle = billingCycleOptions.find((o) => o.value === billingCycle) || billingCycleOptions[0];
  const months = selectedCycle.months;
  const basePrice = STATIC_PLAN.monthlyRate * months;
  const discountPercent = appliedCoupon?.percent || 0;
  const discountAmount = Math.round((basePrice * discountPercent) / 100);
  const discountedSubtotal = basePrice - discountAmount;
  const gstAmount = Math.round(discountedSubtotal * 0.18);
  const totalAmount = discountedSubtotal + gstAmount;

  const handleBillingCycleChange = (e) => setBillingCycle(e.target.value);

  const handleApplyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponMessage({ type: 'error', text: 'Please enter a coupon code.' }); return; }
    if (code === 'HOSTSERVICE10') {
      setAppliedCoupon({ code, percent: 10 });
      setCouponMessage({ type: 'success', text: `Coupon "${code}" applied — 10% off!` });
    } else {
      setAppliedCoupon(null);
      setCouponMessage({ type: 'error', text: 'Invalid coupon code.' });
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponMessage({ type: '', text: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const cleanPhone = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, phone: cleanPhone }));
      if (formError) setFormError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError('');
  };

  const loadCashfreeScript = () =>
    new Promise((resolve) => {
      if (window.Cashfree) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const updatePaymentStatusWithBackend = async (orderId, paymentStatus, cashfreeOrderIdForDisplay) => {
    try {
      const res = await fetch(`${BASE_URL}${API_ENDPOINTS.UPDATE_PAYMENT_STATUS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, payment_status: paymentStatus }),
      });
      const result = await res.json();
      if (!res.ok || result.responseCode !== '200') throw new Error(result.responseMessage || 'Could not update payment status.');
      const updated = result.data || {};
      if (paymentStatus === 'Success' && updated.order_status === 'Completed') {
        setVerifyingPayment(false);
        setPaymentId(cashfreeOrderIdForDisplay || updated.order_id || orderId);
        setPaymentSuccess(true);
        return;
      }
      setVerifyingPayment(false);
      setFormError(
        `We could not confirm your payment (status: ${updated.order_status || updated.payment_status || 'unknown'}). ` +
        `If money was deducted, please check My Orders in a few minutes before retrying.`
      );
    } catch (err) {
      setVerifyingPayment(false);
      setFormError(err.message || 'Could not update your payment status. Please contact support with your order ID: ' + orderId);
    }
  };

  const handleCashfreePayment = async (e) => {
    e.preventDefault();
    setFormError('');
    setAmountError('');

    // Validate the custom amount first
    if (!customAmount.trim() || isNaN(parseFloat(customAmount)) || parseFloat(customAmount) <= 0) {
      setAmountError('Please enter a valid payment amount greater than 0.');
      return;
    }

    if (!formData.fullName.trim()) { setFormError('Please enter your Full Name.'); return; }
    if (!formData.email.trim() || !formData.email.includes('@')) { setFormError('Please enter a valid Email Address.'); return; }
    if (!formData.phone.trim() || formData.phone.length !== 10) { setFormError('Please enter a valid 10-digit Phone / WhatsApp Number.'); return; }
    if (!formData.password || formData.password.length < 6) { setFormError('Please create an account password with at least 6 characters.'); return; }
    if (formData.password !== formData.confirmPassword) { setFormError('Password and Confirm Password do not match.'); return; }
    if (!formData.serverHostname.trim()) { setFormError('Please enter a Hostname for your VPS server.'); return; }
    if (!formData.selectedOS) { setFormError('Please select an Operating System.'); return; }

    setLoadingCashfree(true);

    try {
      let order;
      try {
        const res = await fetch(`${BASE_URL}${API_ENDPOINTS.CREATE_CASHFREE_ORDER}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: payableAmount,          // user-entered amount sent directly
            currency: 'INR',
            customerName: formData.fullName.trim(),
            customerEmail: formData.email.trim(),
            customerPhone: formData.phone,
            planName: `${STATIC_PLAN.name} - ${selectedCycle.label}${appliedCoupon ? ` (Coupon: ${appliedCoupon.code})` : ''}`,
          }),
        });
        const result = await res.json();
        if (!res.ok || result.responseCode !== '200' || !result.data?.payment_session_id) {
          throw new Error(result.responseMessage || 'Could not create payment order.');
        }
        order = result.data;
      } catch (err) {
        console.error('[Cashfree] create-order failed:', err);
        setLoadingCashfree(false);
        setFormError(
          err.message === 'Failed to fetch'
            ? 'Could not reach the payment server. Please check your connection and try again.'
            : (err.message || 'Something went wrong creating your payment order.')
        );
        return;
      }

      const scriptLoaded = await loadCashfreeScript();
      if (!scriptLoaded || !window.Cashfree) {
        setLoadingCashfree(false);
        setFormError('Could not load the Cashfree payment SDK. Please check your connection and try again.');
        return;
      }

      const cashfree = window.Cashfree({ mode: 'production' });
      const result = await cashfree.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: '_modal',
      });

      setLoadingCashfree(false);

      if (result && result.error) {
        setFormError('Payment was cancelled or failed before completion.');
        return;
      }

      setVerifyingPayment(true);
      updatePaymentStatusWithBackend(order.order_id, 'Success', order.cashfree_order_id);
    } catch (err) {
      console.error('[Cashfree] Unexpected error in payment flow:', err);
      setLoadingCashfree(false);
      setFormError('Something went wrong starting the payment. Please try again, or contact support if this keeps happening.');
    }
  };

  const passwordsMatch    = formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordsMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">

        {/* Back Link & Breadcrumbs */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: C.textSecondary, fontWeight: 600, textTransform: 'none', mb: 2, '&:hover': { color: C.primary, background: C.primaryLight } }}
          >
            Back to Home
          </Button>

          <Breadcrumbs separator=">" aria-label="breadcrumb" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
              Home
            </MuiLink>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: C.primary }}>
              Server Checkout
            </Typography>
          </Breadcrumbs>

          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            Server Checkout & Account Provisioning
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 720 }}>
            Enter your account information and server details to deploy your VPS instantly.
          </Typography>
        </Box>

        <Grid container spacing={4}>

          {/* Left Column: Order Summary */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} sx={{
              borderRadius: '16px', overflow: 'hidden', border: `2px solid ${C.primary}`,
              position: 'sticky', top: 90, background: '#FFFFFF',
              boxShadow: '0 10px 30px rgba(37,99,235,0.08)',
            }}>
              <Box sx={{ background: '#0F172A', color: '#FFFFFF', p: 3 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Chip label={STATIC_PLAN.category} size="small" sx={{ background: C.primary, color: '#FFFFFF', fontWeight: 700 }} />
                  <Chip
                    icon={<VerifiedUser sx={{ fontSize: 14, color: '#10B981 !important' }} />}
                    label="99.99% Uptime"
                    size="small"
                    sx={{ background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', fontWeight: 600 }}
                  />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mb: 0.5, fontSize: '1.35rem' }}>
                  {STATIC_PLAN.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                  {STATIC_PLAN.specsText}
                </Typography>
              </Box>

              <Box sx={{ p: 3.5 }}>
                <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                  Included Server Features
                </Typography>
                <Stack spacing={1.25} sx={{ mb: 3 }}>
                  {STATIC_PLAN.features.map((feat, idx) => (
                    <Stack key={idx} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircle sx={{ fontSize: 18, color: C.success }} />
                      <Typography variant="body2" sx={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.88rem' }}>
                        {feat}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ borderColor: C.primary, borderStyle: 'dashed', my: 2.5 }} />

                {/* Total Payable Box */}
                <Box sx={{ p: 2.5, borderRadius: '12px', background: C.primaryLight, border: `1px solid ${C.primary}30`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: C.textMuted, fontWeight: 700, textTransform: 'uppercase' }}>
                      Total Due Today
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.primary, fontSize: '0.78rem', fontWeight: 700 }}>
                      Includes 18% GST & Setup
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '1.75rem', color: C.primary }}>
                    {payableAmount > 0 ? `Rs.${payableAmount.toLocaleString('en-IN')}` : '—'}
                  </Typography>
                </Box>

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ShieldOutlined sx={{ color: C.primary, fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: C.textSecondary, fontWeight: 600 }}>
                      30-Day Money Back Guarantee
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Speed sx={{ color: C.primary, fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: C.textSecondary, fontWeight: 600 }}>
                      Instant Server Provisioning after payment
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Checkout Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '16px' }}>
              {formError && (
                <Alert severity="error" sx={{ mb: 3.5, borderRadius: '10px', fontWeight: 700 }}>
                  {formError}
                </Alert>
              )}

              <form onSubmit={handleCashfreePayment}>
                <Stack spacing={4}>

                  {/* Step 1: Account Details */}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        1
                      </Box>
                      Account Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 2.5, ml: 5 }}>
                      Your login credentials to manage your VPS server
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Full Name *" name="fullName" value={formData.fullName} onChange={handleInputChange} required
                          InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Email Address *" name="email" type="email" value={formData.email} onChange={handleInputChange} required
                          InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Phone Number *" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="10-digit number"
                          InputProps={{ startAdornment: <InputAdornment position="start"><PhoneOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Password *" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleInputChange} required
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment>,
                            endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>,
                          }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Confirm Password *" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleInputChange} required
                          error={passwordsMismatch}
                          helperText={passwordsMismatch ? 'Passwords do not match' : passwordsMatch ? 'Passwords match' : ''}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><KeyOutlined sx={{ color: C.textMuted }} /></InputAdornment>,
                            endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>,
                          }} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: C.border }} />

                  {/* Step 2: Server Configuration */}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        2
                      </Box>
                      Server Configuration
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 2.5, ml: 5 }}>
                      Set your hostname and preferred Linux distribution
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth label="Server Hostname *" name="serverHostname" value={formData.serverHostname} onChange={handleInputChange} required placeholder="e.g. my-vps-server"
                          InputProps={{ startAdornment: <InputAdornment position="start"><LanguageOutlined sx={{ color: C.textMuted }} /></InputAdornment> }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth required>
                          <InputLabel shrink>Operating System *</InputLabel>
                          <Select
                            name="selectedOS" value={formData.selectedOS} label="Operating System *"
                            onChange={handleInputChange} displayEmpty
                            renderValue={(selected) => selected ? selected : <span style={{ color: '#9CA3AF' }}>Select an Operating System</span>}
                          >
                            {osOptions.map((os) => <MenuItem key={os} value={os}>{os}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: C.border }} />

                  {/* Step 3: Payment Amount */}
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: C.primary, color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        3
                      </Box>
                      Payment Amount
                    </Typography>
                    <Typography variant="caption" sx={{ color: C.textMuted, display: 'block', mb: 2.5, ml: 5 }}>
                      Enter the exact amount you want to pay (in INR)
                    </Typography>

                    <TextField
                      fullWidth
                      label="Amount (INR) *"
                      placeholder="e.g. 467"
                      value={customAmount}
                      onChange={handleAmountChange}
                      error={Boolean(amountError)}
                      helperText={amountError || (payableAmount > 0 ? `You will be charged Rs. ${payableAmount.toLocaleString('en-IN')}` : '')}
                      inputProps={{ inputMode: 'decimal', pattern: '[0-9]*\.?[0-9]{0,2}' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CurrencyRupee sx={{ color: C.primary, fontSize: 22 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontSize: '1.2rem',
                          fontWeight: 800,
                          fontFamily: 'monospace',
                          background: payableAmount > 0 ? `${C.primaryLight}` : undefined,
                        },
                        '& .MuiInputLabel-root': { fontWeight: 700 },
                      }}
                    />
                  </Box>

                  <Divider sx={{ borderColor: C.border }} />

                  {/* Step 4: Pay Button */}
                  <Box>
                    <Button
                      type="submit"
                      disabled={loadingCashfree || verifyingPayment || payableAmount <= 0}
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={(loadingCashfree || verifyingPayment) ? <CircularProgress size={22} color="inherit" /> : <AccountBalanceWallet sx={{ fontSize: 24 }} />}
                      sx={{ py: 2, background: C.primary, color: '#FFFFFF', fontWeight: 800, fontSize: '1.1rem', borderRadius: '12px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover }, '&.Mui-disabled': { background: '#CBD5E1', color: '#94A3B8' } }}
                    >
                      {loadingCashfree
                        ? 'Processing...'
                        : verifyingPayment
                          ? 'Confirming your payment...'
                          : payableAmount > 0
                            ? `Pay Now  Rs. ${payableAmount.toLocaleString('en-IN')}`
                            : 'Enter Amount to Pay'}
                    </Button>
                  </Box>

                </Stack>
              </form>
            </Paper>
          </Grid>

        </Grid>
      </Container>

      {/* Success Dialog */}
      <Dialog open={paymentSuccess} onClose={() => navigate('/account/orders')} PaperProps={{ sx: { borderRadius: '16px', p: 3, maxWidth: 480 } }}>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircle sx={{ color: C.success, fontSize: 64, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: C.textPrimary, mb: 1 }}>
            Server Order Placed!
          </Typography>
          <Typography sx={{ color: C.textSecondary, mb: 3, fontSize: '0.92rem' }}>
            Thank you! Your VPS server is being provisioned. Your Transaction ID is:
          </Typography>
          <Chip label={paymentId} sx={{ fontFamily: 'monospace', fontWeight: 800, background: C.primaryLight, color: C.primary, fontSize: '0.9rem', mb: 3 }} />
          <Button fullWidth variant="contained" onClick={() => navigate('/account/orders')} sx={{ background: C.primary, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
            Go to My Server Dashboard
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CheckoutDirect;
